import React, { useEffect, useRef } from 'react';
import { Box, Collapse, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { ReactComponent as AllegrilIcon } from '../../assets/images/psp/allegril.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info.svg';
import { Button } from '../UI-CHAKRA/Button';
import { FreeSampleDeliveryAddress } from '../../interfaces/freeSample';
import { buildAddressSummary } from './freeSampleService';
import FreeSampleHeader from './FreeSampleHeader';

interface FreeSampleSuccessStepProps {
  brand: string;
  medicineNames: string[];
  address: FreeSampleDeliveryAddress;
  pharmaceuticalPageUrl?: string;
  pharmaceuticalPhone?: string;
  onClose: () => void;
  // Evento sample_request_completed (Amplitude): uma vez por exibição da confirmação.
  onRequestCompletedView: () => void;
}

const SummaryRow: React.FC<{ label: string; value: string; noBorder?: boolean }> = ({ label, value, noBorder }) => (
  <Box padding={'16px 0'} borderBottom={noBorder ? 'none' : '1px solid #E9ECEF'}>
    <Text fontSize={'14px'} color={'#6C757D'}>
      {label}
    </Text>
    <Text fontSize={'14px'} fontWeight={700} color={'#343A40'}>
      {value}
    </Text>
  </Box>
);

// Tela de sucesso usada tanto para SAMPLE_REQUEST_CONFIRMED quanto SAMPLE_REQUEST_UNDER_REVIEW —
// pro médico, os dois significam a mesma coisa: "enviei, o laboratório decide" (Figma, seção
// "Médico já cadastrado e vinculado a amostra grátis"). O caminho de quem não é elegível usa
// FreeSampleInterestSuccessStep, não esta tela.
const FreeSampleSuccessStep: React.FC<FreeSampleSuccessStepProps> = ({
  brand,
  medicineNames,
  address,
  pharmaceuticalPageUrl,
  pharmaceuticalPhone,
  onClose,
  onRequestCompletedView,
}) => {
  const hasEmittedViewEventRef = useRef(false);

  useEffect(() => {
    if (hasEmittedViewEventRef.current) return;
    hasEmittedViewEventRef.current = true;
    onRequestCompletedView();
  }, [onRequestCompletedView]);

  const { isOpen, onToggle } = useDisclosure();
  const count = medicineNames.length;
  const countLabel = count === 1 ? '1 amostra' : `${count} amostras`;

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <FreeSampleHeader onClose={onClose} />

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Flex flexDirection={'column'} alignItems={'center'} justifyContent={'center'} gap={'16px'} padding={'20px 0'}>
          <AllegrilIcon width={'130px'} height={'130px'} />

          <Text fontSize={'26px'} fontWeight={700} color={'#343A40'} textAlign={'center'}>
            Solicitação enviada
          </Text>

          <Text fontSize={'14px'} color={'#6C757D'} textAlign={'center'}>
            O laboratório {brand} avalia a disponibilidade e realiza o envio.
          </Text>
        </Flex>

        <Box mt={'12px'}>
          <Flex
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            padding={'16px 0'}
            borderBottom={'1px solid #E9ECEF'}
          >
            <Box>
              <Text fontSize={'14px'} color={'#6C757D'}>
                Solicitação
              </Text>
              <Text fontSize={'14px'} fontWeight={700} color={'#343A40'}>
                {countLabel} de {brand}
              </Text>
            </Box>
            <Flex alignItems={'center'} gap={'2px'} cursor={'pointer'} onClick={onToggle}>
              <Text fontSize={'13px'} fontWeight={700} color={'primary.main'}>
                {isOpen ? 'Recolher' : 'Expandir'}
              </Text>
              {isOpen ? (
                <ChevronUpIcon boxSize={'18px'} color={'primary.main'} />
              ) : (
                <ChevronDownIcon boxSize={'18px'} color={'primary.main'} />
              )}
            </Flex>
          </Flex>
          <Collapse in={isOpen}>
            <Box padding={'12px 0'} borderBottom={'1px solid #E9ECEF'}>
              {medicineNames.map((name) => (
                <Text key={name} fontSize={'13px'} color={'#6C757D'} mb={'4px'}>
                  • {name}
                </Text>
              ))}
            </Box>
          </Collapse>

          <SummaryRow label={'Envio por'} value={brand} />
          <SummaryRow label={'Entrega em'} value={buildAddressSummary(address)} noBorder />
        </Box>

        <Flex flexDirection={'row'} gap={'12px'} backgroundColor={'#EEF1FF'} borderRadius={'10px'} padding={'16px'} mt={'20px'}>
          <Box flexShrink={0} mt={'2px'}>
            <InfoIcon width={'16px'} height={'16px'} fill="#5C2E8C" />
          </Box>
          <Box flex={1}>
            <Text fontSize={'14px'} fontWeight={700} color={'#343A40'} mb={'4px'}>
              A entrega é de responsabilidade do laboratório
            </Text>
            <Text fontSize={'13px'} color={'#343A40'} mb={'8px'}>
              A Mevo encaminha sua solicitação de amostra. O envio e o prazo são do laboratório.
            </Text>
            {(pharmaceuticalPageUrl || pharmaceuticalPhone) && (
              <Text fontSize={'13px'} color={'#6C757D'}>
                Em caso de dúvidas, fale com {brand}
              </Text>
            )}
            {pharmaceuticalPageUrl && (
              <Text
                as="a"
                href={pharmaceuticalPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                display={'block'}
                fontSize={'13px'}
                fontWeight={700}
                color={'primary.main'}
              >
                {pharmaceuticalPageUrl}
              </Text>
            )}
            {pharmaceuticalPhone && (
              <Text
                as="a"
                href={`tel:${pharmaceuticalPhone}`}
                display={'block'}
                fontSize={'13px'}
                fontWeight={700}
                color={'primary.main'}
              >
                {pharmaceuticalPhone}
              </Text>
            )}
          </Box>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onClose}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Fechar
            </Text>
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSampleSuccessStep;
